import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import {
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import ConfirmationDialog from '../common/ConfirmationDialog';
import AddSectionModal from '../atoms/AddSectionModal';
import ExplanationModal from '../atoms/ExplanationModal';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import useFullScreen from '../../hooks/useFullScreen';
import useFieldValidationCRUD from '../../hooks/useFieldValidationCRUD';
import useContentActions from '../../hooks/useContentActions';
import useFieldValidationVersions from '../../hooks/useFieldValidationVersions';
import useExplain from '../../hooks/useExplain';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import Accordion from '../atoms/Accordion';
import { ScrollableBox, EditableTextField } from '../atoms';
import EmptyStateBox from '../atoms/EmptyStateBox';
import LoadingBox from '../atoms/LoadingBox';
import LoadMoreButton from '../atoms/LoadMore';
import SectionHeader from '../atoms/Header';
import TopBarButtons from '../atoms/TopBarButtons';
import VersionSelector from '../atoms/VersionSelector';
import useParsedSections from '../../hooks/useParsedSections';
import {
  extractFieldValidations,
  formatValidationType,
  formatFieldForCopy
} from '../../utils/fieldValidationParser';

/**
 * A split-view layout component that shows conversation on the left
 * and field validation rules in a structured format on the right
 */
const SplitViewFieldValidations = ({
  conversation,
  children,
  isStreaming,
  streamingContent,
  projectId,
  onConversationUpdate
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [parsedGroups, setParsedGroups] = useState([]);
  const [expandedFields, setExpandedFields] = useState({});
  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedFields, setExpandedFields);

  const [visibleValidations, setVisibleValidations] = useState(10);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);

  const { showNotification } = useNotification();
  const validationPanelRef = useRef(null);
  const isLocalUpdateRef = useRef(false);

  // useParsedSections hook — fetches from backend /parsed-responses/latest
  const {
    sections: rawSections,
    setSections: setRawSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // Convert backend generic sections to field validation format if needed.
  // Backend returns {id, title, content, category} but the renderer needs
  // {name, label, type, validations, group}. If sections already have 'name'
  // field, they're already in the right format (from extractFieldValidations).
  const [parsedValidations, setParsedValidations] = React.useState([]);
  React.useEffect(() => {
    if (rawSections.length === 0) {
      setParsedValidations([]);
      return;
    }
    // Check if sections are already in field format
    if (rawSections[0]?.name || rawSections[0]?.validations) {
      setParsedValidations(rawSections);
      return;
    }
    // Generic sections from backend — convert using extractFieldValidations
    // Reconstruct markdown from sections and re-parse
    const markdown = rawSections
      .filter(s => {
        // Filter out non-field-validation sections (design guidelines that leaked in)
        const title = (s.title || '').toLowerCase();
        return s.details?.name || title.includes('field') || title.includes('cursor') ||
          (s.content || '').includes('**Name:**');
      })
      .map(s => `### ${s.title}\n\n${s.rawContent || s.content || ''}`)
      .join('\n\n---\n\n');
    if (markdown.trim()) {
      const { fields } = extractFieldValidations(markdown);
      if (fields.length > 0) {
        setParsedValidations(fields);
        return;
      }
    }
    // Fallback: use raw sections as-is
    setParsedValidations(rawSections);
  }, [rawSections]);

  // Use version management for field validations
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useFieldValidationVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Initialize validation state for fields with version-specific IDs
  const initialValidationState = parsedValidations.reduce((acc, field) => {
    // Create version-specific validation ID
    const versionSpecificId = `${field.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook for marking fields as validated
  const {
    validatedItems: validatedFieldsBase,
    handleValidateItem: handleValidateFieldBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'field-validations',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'field-validations',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateField = (fieldId) => {
    // Create version-specific ID
    const versionSpecificId = `${fieldId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateFieldBase(versionSpecificId);
  };

  // Map validation state to current version
  const validatedFields = Object.keys(validatedFieldsBase).reduce((acc, key) => {
    // Extract field ID from version-specific key
    const fieldId = key.replace(/_v\d+$/, '');
    const versionMatch = key.match(/_v(\d+)$/);
    const version = versionMatch ? parseInt(versionMatch[1]) : 0;

    // Only include validations for current version
    if (version === currentVersionIndex) {
      acc[fieldId] = validatedFieldsBase[key];
    }
    return acc;
  }, {});

  // Use full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Use field validation CRUD hook
  const {
    editingField,
    editedContent,
    editedTitle,
    setEditedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteField,
    handleDeleteFieldConfirmed,
    handleCopyField,
    handleAddField
  } = useFieldValidationCRUD(
    conversation,
    parsedValidations,
    setParsedValidations,
    contentRef,
    onConversationUpdate,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef
  );

  // Use content actions hook for export, copy, etc.
  const {
    isExporting,
    handleExport,
    getExportContent,
    handleCopy: handleCopyAll
  } = useContentActions(contentRef, 'Field Validations', projectId, conversation?._id);

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainField,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Determine if we should show the split view
  const isFieldValidationsConversation = conversation && (conversation?.taskId || conversation?.task_id) === 'field-validations';

  // Create a wrapper function for validation that adds notifications
  const handleValidateFieldWithNotification = async (fieldId, fieldName) => {
    try {
      await handleValidateField(fieldId);
    } catch (error) {
      console.error('Error in validation handler:', error);
    }
  };

  // Handler for explaining a field validation
  const handleExplainField = (field) => {
    // Format field data for explanation
    const formattedField = {
      title: field.label || field.name,
      content: formatFieldForCopy(field), // Use the existing formatter to create a readable format
      rawContent: formatFieldForCopy(field)
    };
    requestExplanation(formattedField);
  };

  // Version switching: reset visible validations when switching versions
  useEffect(() => {
    if (currentVersion) {
      const versionChanged = prevVersionIndexRef.current !== currentVersionIndex;
      if (versionChanged) {
        setVisibleValidations(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Auto-scroll validation panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && validationPanelRef.current && isFieldValidationsConversation) {
      setTimeout(() => {
        if (validationPanelRef.current) {
          validationPanelRef.current.scrollTop = validationPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isFieldValidationsConversation]);

  // Handler for copying all validations to clipboard with HTML table formatting
  const handleCopyValidations = async () => {
    try {
      if (!parsedValidations || parsedValidations.length === 0) {
        showNotification({
          message: "No field validation content to copy",
          type: "warning"
        });
        return;
      }

      let allHtml = '<h2>Field Validations</h2>';
      let allText = "Field Validations\n" + "=".repeat(70) + "\n\n";

      parsedValidations.forEach((field, index) => {
        const { html, text } = formatFieldForCopy(field);
        allHtml += `<h3>${index + 1}. ${(field.label || field.name).replace(/&/g, '&amp;').replace(/</g, '&lt;')}</h3>${html}<hr>`;
        allText += `${index + 1}. ${text}\n${'='.repeat(70)}\n\n`;
      });

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([allHtml], { type: 'text/html' }),
          'text/plain': new Blob([allText], { type: 'text/plain' })
        })
      ]);
      showNotification({
        message: `${parsedValidations.length} field validation${parsedValidations.length !== 1 ? 's' : ''} copied to clipboard`,
        type: "success"
      });
    } catch (error) {
      console.error("Error copying field validations:", error);
      showNotification({
        message: "Failed to copy field validations to clipboard",
        type: "error"
      });
    }
  };

  // Handler for adding new field validation
  const handleAddNewFieldValidation = async (modalData) => {
    try {
      // Check if we're on the latest version
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new field validations',
          type: 'warning'
        });
        setShowAddFieldModal(false);
        return;
      }

      // Transform modal data to field validation format
      const fieldData = {
        name: modalData.title || modalData.name || 'newField',
        label: modalData.title || modalData.name || 'New Field',
        type: modalData.rawData?.type || 'Text',
        description: modalData.rawData?.description || modalData.content?.split('\n')[0] || '',
        group: modalData.rawData?.group || 'General',
        validations: modalData.rawData?.validations || [
          {
            type: 'required',
            value: true,
            message: 'This field is required'
          }
        ],
        implementation: modalData.rawData?.implementation || ''
      };

      // Call the CRUD hook's handleAddField
      const newField = await handleAddField(fieldData);

      if (newField) {
        // Set the flag to prevent reset from currentVersion
        isLocalUpdateRef.current = true;

        // Expand the new field by default
        setExpandedFields(prev => ({ ...prev, [newField.id]: true }));

        // Ensure the new field is visible
        if (visibleValidations < parsedValidations.length + 1) {
          setVisibleValidations(parsedValidations.length + 1);
        }

        // Scroll to bottom to show new field
        setTimeout(() => {
          if (validationPanelRef.current) {
            validationPanelRef.current.scrollTop = validationPanelRef.current.scrollHeight;
          }
        }, 100);
      }

      // Close modal
      setShowAddFieldModal(false);
    } catch (error) {
      console.error('Error in handleAddNewFieldValidation:', error);
    }
  };

  // Determine if we should show the validation panel
  const showFieldPanel = parsedValidations.length > 0;

  // Don't show the split view if this isn't a field validations conversation
  if (!isFieldValidationsConversation || (!showFieldPanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Determine if there are more validations to load
  const hasMoreValidations = visibleValidations < parsedValidations.length;
  const visibleValidationsList = parsedValidations.slice(0, visibleValidations);

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with split layout */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel (left) */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          overflow: 'auto',
          transition: 'flex 0.3s ease',
          height: '100%',
          p: 2,
        }}>
          {children}
        </Box>

        {/* Field validations panel (right) */}
        <Box
          ref={validationPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            overflow: 'auto',
            transition: 'flex 0.3s ease',
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {/* Header section */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Field validations' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      console.log('🔄 FieldValidations: Version changed - reset isLocalUpdateRef');
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  onAdd={isLatestVersion ? () => setShowAddFieldModal(true) : null}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add field validations" : "Add new field validation"}
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  getExportContent={getExportContent}
                  onExport={handleExport}
                  isExporting={isExporting}
                  onCopy={handleCopyValidations}
                  onRefresh={() => {
                    showNotification({
                      message: "Refreshing field validations...",
                      type: "info"
                    });
                  }}
                  disabled={!contentRef.current || !parsedValidations.length}
                  exportTooltip="Export field validations as markdown"
                  copyTooltip="Copy field validations to clipboard"
                  refreshTooltip="Refresh field validations"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'field-validations'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedFieldsBase}
              totalItems={rawSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Content section */}
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show parsed validations or loading/empty state */}
              {isStreaming ? (
                <LoadingBox message="Generating field validation rules..." />
              ) : parsedValidations.length > 0 ? (
                <Box>
                  {/* Render field validations grouped by category */}
                  {(() => {
                    // Group fields by category
                    const groupedFields = {};
                    visibleValidationsList.forEach(field => {
                      const group = field.group || 'General';
                      if (!groupedFields[group]) {
                        groupedFields[group] = [];
                      }
                      groupedFields[group].push(field);
                    });

                    let globalFieldIndex = 0;
                    return Object.keys(groupedFields).map((groupName, groupIndex) => (
                      <Box key={groupIndex} sx={{ mb: 4 }}>
                        {/* Category Header - Clean Style matching Features */}
                        <Box sx={{
                          mt: groupIndex > 0 ? 4 : 0,
                          mb: 2,
                          px: 2
                        }}>
                          <SectionHeader label={groupName} />
                        </Box>


                        {/* Fields under this category */}
                        {groupedFields[groupName].map((field, fieldIndex) => {
                          globalFieldIndex++;
                          const fieldId = field.id || `field-${fieldIndex}`;
                          const isValidated = validatedFields[fieldId];

                          // Create clean title without section prefix, just field name
                          const cleanTitle = field.label || field.name;

                          return (
                            <Accordion
                              key={fieldId}
                              ref={shouldHighlight(fieldId) ? highlightRef : null}
                              title={cleanTitle}
                              defaultExpanded={false}
                              expanded={expandedFields[fieldId]}
                              onChange={(_, expanded) => setExpandedFields(prev => ({
                                ...prev,
                                [fieldId]: expanded
                              }))}
                              rightIcon={
                                isValidated ? (
                                  <ValidatedIcon color="success" fontSize="small" memberType={getValidatorType(fieldId)} />
                                ) : (
                                  <UnvalidatedIcon color="disabled" fontSize="small" />
                                )
                              }
                              onRightIconClick={(e) => {
                                e.stopPropagation();
                                handleValidateFieldWithNotification(fieldId, field.label || field.name);
                              }}
                              titleContent={editingField === fieldId ? (
                                <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                                  <TextField
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.target.removeAttribute('title')}
                                    size="small"
                                    fullWidth
                                    placeholder="Enter field validation title"
                                    variant="outlined"
                                    InputProps={{ title: '' }}
                                    inputProps={{ title: '', autoComplete: 'off' }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        backgroundColor: '#fff',
                                        '& fieldset': { borderWidth: '1px' },
                                        '&:hover fieldset': { borderWidth: '1px' },
                                        '&.Mui-focused fieldset': { borderWidth: '1px' },
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        padding: '8px 12px',
                                      },
                                    }}
                                  />
                                </Box>
                              ) : null}
                              sx={{ mb: 3 }}
                            >
                              <ScrollableBox
                                sx={{
                                  textAlign: 'justify',
                                  cursor: editingField === fieldId ? 'default' : 'auto',
                                }}
                              >
                                {/* Show edit mode or normal content */}
                                {editingField === fieldId ? (
                                  // Edit mode
                                  <EditableTextField
                                    value={editedContent}
                                    onChange={(e) => setEditedContent(e.target.value)}
                                  />
                                ) : (
                                  // Normal content display
                                  <Box ref={getSectionContentRef(fieldId)}>
                                    <TextSelectionPopup
                                      containerRef={getSectionContentRef(fieldId)}
                                      sectionId={fieldId}
                                      onSubmitComment={addComment}
                                      disabled={editingField === fieldId}
                                    />
                                    {/* Field description */}
                                    {field.description && (
                                      <Box sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                                          {field.description}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Single validation table with Type, Value, Error Message */}
                                    {field.validations && field.validations.length > 0 ? (
                                      <TableContainer>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{
                                                fontSize: "14px !important",
                                                fontWeight: "bold !important",
                                                width: '25%',
                                                backgroundColor: '#f5f8fb',
                                                borderBottom: '2px solid #e0e0e0',
                                                padding: '12px 16px'
                                              }}>
                                                Type
                                              </TableCell>
                                              <TableCell sx={{
                                                fontSize: "14px !important",
                                                fontWeight: "bold !important",
                                                width: '30%',
                                                backgroundColor: '#f5f8fb',
                                                borderBottom: '2px solid #e0e0e0',
                                                padding: '12px 16px'
                                              }}>
                                                Value
                                              </TableCell>
                                              <TableCell sx={{
                                                fontSize: "14px !important",
                                                fontWeight: "bold !important",
                                                width: '45%',
                                                backgroundColor: '#f5f8fb',
                                                borderBottom: '2px solid #e0e0e0',
                                                padding: '12px 16px'
                                              }}>
                                                Error Message / Visual Indicator
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {/* Validation rules */}
                                            {field.validations.map((validation, vIndex) => (
                                              <TableRow
                                                key={vIndex}
                                                sx={{
                                                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                                  '&:nth-of-type(odd)': { backgroundColor: 'white' },
                                                  '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                                                }}
                                              >
                                                <TableCell sx={{
                                                  fontSize: "14px !important",
                                                  fontWeight: 'bold',
                                                  padding: '12px 16px',
                                                  borderBottom: '1px solid #e0e0e0'
                                                }}>
                                                  {formatValidationType(validation.type)}
                                                </TableCell>
                                                <TableCell sx={{
                                                  fontSize: "14px !important",
                                                  padding: '12px 16px',
                                                  borderBottom: '1px solid #e0e0e0'
                                                }}>
                                                  {validation.value === true ? (
                                                    <Typography variant="body2" sx={{ fontSize: "14px !important" }}>
                                                      Yes
                                                    </Typography>
                                                  ) : validation.value === false ? (
                                                    <Typography variant="body2" sx={{ fontSize: "14px !important" }}>
                                                      No
                                                    </Typography>
                                                  ) : (
                                                    <Typography variant="body2" sx={{ fontSize: "14px !important" }}>
                                                      {typeof validation.value === 'string' && validation.value.length > 50 ? (
                                                        <code style={{
                                                          fontSize: '12px',
                                                          backgroundColor: '#f0f0f0',
                                                          padding: '4px 8px',
                                                          borderRadius: '3px',
                                                          display: 'inline-block',
                                                          wordBreak: 'break-word',
                                                          fontFamily: 'monospace'
                                                        }}>
                                                          {validation.value}
                                                        </code>
                                                      ) : typeof validation.value === 'string' ? (
                                                        validation.value
                                                      ) : (
                                                        validation.value
                                                      )}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell sx={{
                                                  fontSize: "14px !important",
                                                  padding: '12px 16px',
                                                  borderBottom: '1px solid #e0e0e0'
                                                }}>
                                                  "{validation.message || '–'}"
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Box sx={{
                                        p: 3,
                                        textAlign: 'center',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 1,
                                        border: '1px dashed #e0e0e0'
                                      }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "14px !important" }}>
                                          No validation rules defined for this field.
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Implementation example */}
                                    {field.implementation && field.implementation.trim() && (
                                      <Box sx={{ mt: 4, mb: 2 }}>
                                        <Typography
                                          variant="subtitle2"
                                          gutterBottom
                                          sx={{
                                            fontSize: "14px !important",
                                            fontWeight: "bold !important",
                                            mb: 1.5,
                                            color: '#1976d2',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}
                                        >
                                          <Box component="span" sx={{
                                            display: 'inline-block',
                                            width: 4,
                                            height: 16,
                                            backgroundColor: '#1976d2',
                                            borderRadius: '2px'
                                          }}></Box>
                                          Implementation Example
                                        </Typography>
                                        <Box sx={{
                                          backgroundColor: '#f8f9fa',
                                          p: 2.5,
                                          borderRadius: '6px',
                                          border: '1px solid #dee2e6',
                                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                          position: 'relative',
                                          '&::before': {
                                            content: '"JavaScript"',
                                            position: 'absolute',
                                            top: 8,
                                            right: 12,
                                            fontSize: '11px',
                                            color: '#6c757d',
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                          }
                                        }}>
                                          <pre style={{
                                            margin: 0,
                                            overflow: 'auto',
                                            fontSize: "13px",
                                            fontFamily: '"Fira Code", "Courier New", monospace',
                                            lineHeight: '1.6',
                                            color: '#212529',
                                            whiteSpace: 'pre',
                                            wordWrap: 'normal'
                                          }}>
                                            <code>{field.implementation}</code>
                                          </pre>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                              </ScrollableBox>
                              {/* Action buttons */}
                              <Box sx={{
                                position: 'absolute',
                                right: 10,
                                top: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                              }}>
                                {editingField === fieldId ? (
                                  <>
                                    {/* Save button */}
                                    <Box sx={{
                                      mb: 1,
                                      width: 32,
                                      height: 32,
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      backgroundColor: '#f0f0f0',
                                      borderRadius: '4px',
                                    }}>
                                      <Tooltip title="Save" placement="left">
                                        <IconButton
                                          size="small"
                                          sx={{ p: 0.5 }}
                                          color="success"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveEdit(fieldId);
                                          }}
                                        >
                                          <SaveIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                    {/* Cancel button */}
                                    <Box sx={{
                                      mb: 1,
                                      width: 32,
                                      height: 32,
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      backgroundColor: '#f0f0f0',
                                      borderRadius: '4px',
                                    }}>
                                      <Tooltip title="Cancel" placement="left">
                                        <IconButton
                                          size="small"
                                          sx={{ p: 0.5 }}
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelEdit();
                                          }}
                                        >
                                          <CancelIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </>
                                ) : (
                                  <ActionButtons
                                    item={field}
                                    onEdit={() => handleStartEdit(fieldId, field, field.label || field.name || '')}
                                    onExplain={() => handleExplainField(field)}
                                    onCopy={() => handleCopyField(field)}
                                    onDelete={() => handleDeleteField(field)}
                                    vertical={true}
                                    onComment={() => openCommentPanel(fieldId)}
                                    commentCount={commentCounts[fieldId]?.open || 0}
                                    isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(fieldId) : null}
                                  />
                                )}
                              </Box>
                            </Accordion>
                          );
                        })}
                      </Box>
                    ));
                  })()}

                  {/* Load more button */}
                  {hasMoreValidations && (
                    <LoadMoreButton
                      remaining={parsedValidations.length - visibleValidations}
                      onLoadMore={() => setVisibleValidations(prev => Math.min(prev + 10, parsedValidations.length))}
                      onShowAll={() => setVisibleValidations(parsedValidations.length)}
                      label="Load More Validations"
                    />
                  )}
                </Box>
              ) : (
                <EmptyStateBox
                  message="No field validations found in this conversation yet."
                  sx={{ p: 2 }}
                  textSx={{ fontSize: "16px !important" }}
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, field: null })}
        title="Delete Field Validation"
        message={`Are you sure you want to delete the field "${deleteConfirmation.field?.label || deleteConfirmation.field?.name}"?`}
        confirmText="Yes"
        cancelText="No"
        confirmColor="error"
        height="auto"
        onConfirm={handleDeleteFieldConfirmed}
      />

      {/* Add Field Validation Modal */}
      <AddSectionModal
        open={showAddFieldModal}
        onClose={() => setShowAddFieldModal(false)}
        onSave={handleAddNewFieldValidation}
        sectionType="field-validation"
        existingSections={parsedValidations}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainField?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (rawSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewFieldValidations;
